import { cn } from "@/lib/utils";
import { MAP_H, MAP_W } from "./country-geometry";

/* Bones are static; every section wrapper carries the same `animate-pulse`,
   so all placeholders breathe in one phase while the card chrome (borders)
   stays still. The bones sit exactly where the loaded content will, at the
   same sizes, so the page does not reflow when data lands. */
function Bone({ className }: { className?: string }) {
  return <div className={cn("rounded bg-muted", className)} />;
}

/* Card chrome identical to DashboardCard: title row, then the body. */
function CardBone({
  className,
  children,
  wide = false,
}: {
  className?: string;
  children: React.ReactNode;
  /** Title row also shows a "View all" bone on the right. */
  wide?: boolean;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card", className)}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 animate-pulse">
        <Bone className="h-4 w-28" />
        <div className="flex items-center gap-2">
          <Bone className="h-3 w-10" />
          {wide && <Bone className="h-3 w-12" />}
        </div>
      </div>
      {children}
    </div>
  );
}

/* A DataTable placeholder: the 11px uppercase header line, then 40px rows,
   each cell a bone sized to its column's share of the width. */
function TableBone({
  rows,
  columns,
  className,
}: {
  rows: number;
  /** Relative column widths, e.g. [3, 2, 1, 1] */
  columns: number[];
  className?: string;
}) {
  const total = columns.reduce((a, b) => a + b, 0);
  const widths = columns.map((c) => `${(c / total) * 100}%`);
  return (
    <div className={cn("px-3 pb-3 animate-pulse", className)}>
      <div className="flex items-center gap-6 border-b border-border px-2 py-1.5">
        {widths.map((w, i) => (
          <div key={i} style={{ width: w }}>
            <Bone className="h-2.5 w-1/2 max-w-16" />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex h-10 items-center gap-6 border-b border-border px-2 last:border-b-0">
          {widths.map((w, i) => (
            <div key={i} style={{ width: w }}>
              <Bone className={cn("h-3.5", i === 0 ? "w-3/4" : "w-1/2")} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* One cell of the activity strip: label, value, change line. */
function MetricBone() {
  return (
    <div className="flex flex-col gap-1 border-l border-t border-border px-5 py-4">
      <Bone className="my-0.5 h-3.5 w-24" />
      <Bone className="my-1 h-7 w-20" />
      <Bone className="my-0.5 h-3 w-32" />
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="flex-1 space-y-4" aria-busy="true" aria-label="Loading dashboard">
      {/* Page header: title and three actions */}
      <div className="mb-6 flex w-full flex-col justify-between gap-4 animate-pulse sm:mb-8 xl:flex-row">
        <Bone className="h-7 w-32" />
        <div className="flex gap-3">
          <Bone className="h-10 w-36 rounded-md" />
          <Bone className="h-10 w-36 rounded-md" />
          <Bone className="h-10 w-36 rounded-md" />
        </div>
      </div>

      {/* Activity strip */}
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 animate-pulse">
          <Bone className="h-4 w-20" />
          <Bone className="h-7 w-44 rounded-md" />
        </div>
        <div className="-ml-px grid grid-cols-1 animate-pulse sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <MetricBone key={i} />
          ))}
        </div>
      </div>

      {/* Latest sales beside the network card; the network card sets the row
          height and the sales card fills it, exactly as when loaded. */}
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="relative min-w-0 lg:col-span-8">
          <div className="lg:absolute lg:inset-0">
            <CardBone className="lg:h-full">
              <TableBone rows={8} columns={[5, 3, 2, 2, 2]} />
            </CardBone>
          </div>
        </div>
        <div className="min-w-0 lg:col-span-4">
          <CardBone>
            <div className="px-3 animate-pulse">
              <div
                className="w-full rounded-lg bg-muted"
                style={{ aspectRatio: `${MAP_W} / ${MAP_H}` }}
              />
            </div>
            <div className="px-5 pb-2 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between border-t border-border py-3 first:border-t-0"
                >
                  <Bone className="h-3.5 w-24" />
                  <Bone className="my-1 h-5 w-10" />
                </div>
              ))}
            </div>
          </CardBone>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <CardBone wide>
          <TableBone rows={5} columns={[5, 3, 2, 2, 2]} />
        </CardBone>
        <CardBone wide>
          <TableBone rows={5} columns={[6, 2, 2, 2]} />
        </CardBone>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <CardBone wide>
          <TableBone rows={5} columns={[4, 4, 2, 2]} />
        </CardBone>
        <CardBone wide>
          <TableBone rows={5} columns={[4, 2, 2, 2, 2]} />
        </CardBone>
      </div>
      <CardBone wide>
        <TableBone rows={4} columns={[6, 2, 2, 2, 2, 2]} />
      </CardBone>
    </div>
  );
}
